import React, { useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Image, Mic, FileText, X, Check, Loader2 } from 'lucide-react';
import { PROOF_TYPES, capturePhoto, selectImage } from '../hooks/useTaskProofs';
import { Z_CLASS } from '../../../constants/zIndex';
import { cn } from '../../../shared/utils/cn';
import { haptic } from '../../../shared/utils/haptics';

// Proof type options
const PROOF_OPTIONS = [
  { type: PROOF_TYPES.PHOTO, icon: Camera, label: 'Photo', color: 'text-blue-400' },
  { type: PROOF_TYPES.SCREENSHOT, icon: Image, label: 'Image', color: 'text-purple-400' },
  { type: PROOF_TYPES.VOICE, icon: Mic, label: 'Audio', color: 'text-red-400' },
  { type: PROOF_TYPES.TEXT, icon: FileText, label: 'Note', color: 'text-emerald-400' }
];

/**
 * ProofCaptureModal - Modal for capturing task proofs
 */
export const ProofCaptureModal = memo(function ProofCaptureModal({ task, onCapture, onClose }) {
  const [selectedType, setSelectedType] = useState(null);
  const [capturedData, setCapturedData] = useState(null);
  const [textNote, setTextNote] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [loading, setLoading] = useState(false);

  // Capture photo
  const handlePhoto = useCallback(async () => {
    setLoading(true);
    haptic.tap();
    try {
      const data = await capturePhoto();
      setCapturedData(data);
      setSelectedType(PROOF_TYPES.PHOTO);
    } catch (error) {
      console.error('Erreur capture photo:', error);
      haptic.error();
    }
    setLoading(false);
  }, []);

  // Select image
  const handleSelectImage = useCallback(async () => {
    setLoading(true);
    haptic.tap();
    try {
      const data = await selectImage();
      setCapturedData(data);
      setSelectedType(PROOF_TYPES.SCREENSHOT);
    } catch (error) {
      console.error('Erreur sélection image:', error);
      haptic.error();
    }
    setLoading(false);
  }, []);

  // Handle voice recording (simplified)
  const handleVoice = useCallback(() => {
    haptic.tap();
    setSelectedType(PROOF_TYPES.VOICE);
    setIsRecording(true);
    setTimeout(() => {
      setCapturedData('Note vocale enregistrée');
      setIsRecording(false);
      haptic.success();
    }, 2000);
  }, []);

  // Handle text note
  const handleText = useCallback(() => {
    haptic.tap();
    setSelectedType(PROOF_TYPES.TEXT);
  }, []);

  // Save proof
  const handleSave = useCallback(() => {
    if (!selectedType) return;

    haptic.success();
    const proofData = {
      type: selectedType,
      data: selectedType === PROOF_TYPES.TEXT ? textNote : capturedData,
      taskId: task.id,
      taskName: task.task,
      category: task.category,
      date: new Date().toISOString().split('T')[0]
    };

    onCapture(proofData);
    onClose();
  }, [selectedType, capturedData, textNote, task, onCapture, onClose]);

  // Reset
  const handleReset = useCallback(() => {
    haptic.tap();
    setSelectedType(null);
    setCapturedData(null);
    setTextNote('');
    setIsRecording(false);
  }, []);

  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  return (
    <motion.div
      className={cn('fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4', Z_CLASS.PROOF_CAPTURE)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="glass-elevated w-full max-w-md overflow-hidden"
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-base text-zinc-100">Ajouter une preuve</h3>
            <motion.button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <X className="w-4 h-4 text-zinc-400" />
            </motion.button>
          </div>
          <p className="text-sm text-zinc-500 mt-1 truncate">{task.task}</p>
        </div>

        {/* Content */}
        <div className="p-4">
          <AnimatePresence mode="wait">
            {/* Type selection */}
            {!selectedType && !loading && (
              <motion.div
                key="selection"
                className="grid grid-cols-2 gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {PROOF_OPTIONS.map((option, index) => (
                  <motion.button
                    key={option.type}
                    onClick={() => {
                      if (option.type === PROOF_TYPES.PHOTO) handlePhoto();
                      else if (option.type === PROOF_TYPES.SCREENSHOT) handleSelectImage();
                      else if (option.type === PROOF_TYPES.VOICE) handleVoice();
                      else if (option.type === PROOF_TYPES.TEXT) handleText();
                    }}
                    className="glass-interactive p-4 flex flex-col items-center gap-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <option.icon className={cn('w-6 h-6', option.color)} />
                    <span className="font-medium text-sm text-zinc-200">{option.label}</span>
                  </motion.button>
                ))}
              </motion.div>
            )}

            {/* Loading */}
            {loading && (
              <motion.div
                key="loading"
                className="flex flex-col items-center justify-center py-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
                <p className="text-zinc-500 text-sm mt-2">Chargement...</p>
              </motion.div>
            )}

            {/* Photo/Image preview */}
            {(selectedType === PROOF_TYPES.PHOTO || selectedType === PROOF_TYPES.SCREENSHOT) && capturedData && (
              <motion.div
                key="preview"
                className="space-y-3"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <div className="relative rounded-xl overflow-hidden">
                  <img
                    src={capturedData}
                    alt="Preuve capturée"
                    className="w-full h-48 object-cover"
                  />
                  <motion.button
                    onClick={handleReset}
                    className="absolute top-2 right-2 glass-subtle p-1.5 rounded-lg"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="w-3.5 h-3.5 text-white" />
                  </motion.button>
                </div>
                <div className="flex gap-2">
                  <motion.button
                    onClick={handleReset}
                    className="flex-1 py-2.5 px-4 glass-card text-zinc-400 font-medium text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Reprendre
                  </motion.button>
                  <motion.button
                    onClick={handleSave}
                    className="flex-1 py-2.5 px-4 bg-zinc-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Check className="w-4 h-4" />
                    Valider
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Voice recording */}
            {selectedType === PROOF_TYPES.VOICE && (
              <motion.div
                key="voice"
                className="space-y-4 text-center py-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {isRecording ? (
                  <>
                    <motion.div
                      className="w-14 h-14 mx-auto glass-card flex items-center justify-center"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <Mic className="w-6 h-6 text-red-400" />
                    </motion.div>
                    <p className="text-zinc-400 font-medium text-sm">Enregistrement en cours...</p>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 mx-auto glass-card flex items-center justify-center">
                      <Check className="w-6 h-6 text-emerald-400" />
                    </div>
                    <p className="text-zinc-300 font-medium text-sm">Note vocale prête !</p>
                    <div className="flex gap-2">
                      <motion.button
                        onClick={handleReset}
                        className="flex-1 py-2.5 px-4 glass-card text-zinc-400 font-medium text-sm"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Recommencer
                      </motion.button>
                      <motion.button
                        onClick={handleSave}
                        className="flex-1 py-2.5 px-4 bg-zinc-700 text-white rounded-xl font-medium text-sm"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Valider
                      </motion.button>
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* Text note */}
            {selectedType === PROOF_TYPES.TEXT && (
              <motion.div
                key="text"
                className="space-y-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <textarea
                  value={textNote}
                  onChange={(e) => setTextNote(e.target.value)}
                  placeholder="Décrivez ce que vous avez accompli..."
                  className="w-full h-28 p-3 glass-card resize-none focus:ring-1 focus:ring-zinc-600 outline-none text-zinc-200 text-sm placeholder:text-zinc-600"
                  autoFocus
                />
                <div className="flex gap-2">
                  <motion.button
                    onClick={handleReset}
                    className="flex-1 py-2.5 px-4 glass-card text-zinc-400 font-medium text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Annuler
                  </motion.button>
                  <motion.button
                    onClick={handleSave}
                    disabled={!textNote.trim()}
                    className="flex-1 py-2.5 px-4 bg-zinc-700 text-white rounded-xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: textNote.trim() ? 1.02 : 1 }}
                    whileTap={{ scale: textNote.trim() ? 0.98 : 1 }}
                  >
                    Valider
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 text-center border-t border-white/[0.06]">
          <p className="text-[10px] text-zinc-600">
            Données stockées localement
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
});

/**
 * ProofButton - Compact button for adding proof
 */
export const ProofButton = memo(function ProofButton({ onClick, hasProof = false }) {
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    haptic.tap();
    onClick();
  }, [onClick]);

  return (
    <motion.button
      onClick={handleClick}
      className={cn(
        'p-1.5 rounded-lg transition-colors',
        hasProof
          ? 'bg-emerald-500/20 text-emerald-400'
          : 'glass-subtle text-zinc-500 hover:text-zinc-300'
      )}
      title={hasProof ? 'Voir la preuve' : 'Ajouter une preuve'}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
    >
      {hasProof ? (
        <Check className="w-3.5 h-3.5" />
      ) : (
        <Camera className="w-3.5 h-3.5" />
      )}
    </motion.button>
  );
});

/**
 * ProofThumbnail - Miniature proof display
 */
export const ProofThumbnail = memo(function ProofThumbnail({ proof, onClick, onDelete }) {
  const handleClick = useCallback(() => {
    haptic.tap();
    onClick?.();
  }, [onClick]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    haptic.tap();
    onDelete?.(proof.id);
  }, [onDelete, proof.id]);

  return (
    <motion.div
      onClick={handleClick}
      className="relative w-16 h-16 rounded-lg overflow-hidden cursor-pointer glass-card"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {(proof.type === PROOF_TYPES.PHOTO || proof.type === PROOF_TYPES.SCREENSHOT) && proof.data && (
        <img src={proof.data} alt="Preuve" className="w-full h-full object-cover" />
      )}
      {proof.type === PROOF_TYPES.VOICE && (
        <div className="w-full h-full flex items-center justify-center bg-red-500/20">
          <Mic className="w-5 h-5 text-red-400" />
        </div>
      )}
      {proof.type === PROOF_TYPES.TEXT && (
        <div className="w-full h-full flex items-center justify-center bg-emerald-500/20">
          <FileText className="w-5 h-5 text-emerald-400" />
        </div>
      )}
      {proof.type === PROOF_TYPES.TIMER && (
        <div className="w-full h-full flex items-center justify-center bg-indigo-500/20">
          <Check className="w-5 h-5 text-indigo-400" />
        </div>
      )}
      {onDelete && (
        <motion.button
          onClick={handleDelete}
          className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full"
          whileHover={{ scale: 1.1 }}
        >
          <X className="w-3 h-3 text-white" />
        </motion.button>
      )}
    </motion.div>
  );
});

export default ProofCaptureModal;
